import { useState, useContext, useEffect } from "react";
import BorderChangeNotice from "../BorderChangeNotice";
import { Link } from "react-router-dom";
import { successToast, errorToast } from "../../../toastAlerts/ToastAlerts";
import { AdminPrimaryContext } from "../../../adminContext/AdminContext";
import WarehousePackageItem from "./WarehousePackageItem";
import {
  CheckBox,
  SelectInputValueId,
} from "../../../inputComponents/inputcomp";
import axiosAdmin from "../../../../../api/apiAdmin";
import RecipientAddressModal from "../RecipientAddressModal";
import FilialsCardWar from "./FilialsCardWar";
import { useTranslation } from "react-i18next";
import PaginatedItems from "../../../Pagination/Pagination";
import { BiLoaderAlt } from "react-icons/bi";
import { BsX } from "react-icons/bs";
import { apiError } from "../../../../../api/apiError";
import AdminDownloadLink from "../../../AdminDownloadLink";

const WarehousePackage = () => {
  //translations
  const { t } = useTranslation();
  //user
  const { user, scopedOfficeId } = useContext(AdminPrimaryContext);
  //current filial
  const [currentPlaceId, setCurrentPlaceId] = useState(0);
  // Customs flags the last edit changed on OTHER parcels in the group.
  const [borderNotice, setBorderNotice] = useState(null);
  //current place type
  const [currentPlaceType, setCurrentPlaceType] = useState(0);
  //branches data
  const { foreignAdresses } = useContext(AdminPrimaryContext);
  //package data
  const [packageData, setPackageData] = useState([]);
  //package dataErr
  const [packageDataErr, setPackageDataErr] = useState([]);
  //affect data change depended on delete and add responses
  const [difResponses, setDifResponses] = useState(null);
  //search value and transportation
  const [search, setSearch] = useState({
    key: "",
  });
  //package check all
  const [checkAll, setCheckAll] = useState(false);
  //selected Packages
  const [selectedPackages, setSelectedPackages] = useState({
    pack_id: [],
  });
  //current page
  const [currentPage, setCurrentPage] = useState(1);
  //total count
  const [total, setTotal] = useState(0);
  //general shipment code for sending
  const [shipmentGeneralCode, setShipmentGeneralCode] = useState("");
  //is loading state
  const [isLoading, setIsLoading] = useState(false);
  //transportation and secured parcels
  const [transportation, setTransportation] = useState({
    transport: "",
    secured: "",
    repack: "",
  });
  //shipments
  const [shipments, setShipments] = useState([]);

  //handle checking
  const handleCheckAll = (e) => {
    setCheckAll(e.target.checked);
  };

  //handle selected Packages
  const handleSelectedPackages = (value) => {
    if (!selectedPackages.pack_id.includes(value)) {
      setSelectedPackages((prev) => ({ pack_id: [...prev.pack_id, value] }));
    }
  };

  //handle delete selected Packages
  const handleDeleteSelectedPackages = (value) => {
    setSelectedPackages((prev) => ({
      pack_id: prev.pack_id.filter((obj) => {
        return obj !== value;
      }),
    }));
  };

  //handle change search value
  const handleSearchvalue = (e, inputName) => {
    setSearch((prev) => ({ ...prev, [inputName]: e.target.value }));
  };

  //handle transportation value
  const handleSearchTransport = (value, inputName) => {
    setTransportation((prev) => ({ ...prev, [inputName]: value }));
  };

  //current filial handler
  const handleCurrentPlaceId = (value) => {
    setShipmentGeneralCode("");
    setCurrentPlaceId(value);
    setCheckAll(false);
    setSelectedPackages({
      pack_id: [],
    });
    const tmp = foreignAdresses.map((item) => {
      if (item.id === value) {
        setCurrentPlaceType(item.type);
        return null;
      } else {
        return null;
      }
    });
  };

  // Warehouse-scoped admins: auto-select their office once foreignAdresses loads.
  useEffect(() => {
    if (scopedOfficeId && !currentPlaceId && foreignAdresses.length > 0) {
      handleCurrentPlaceId(scopedOfficeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedOfficeId, foreignAdresses.length]);

  //handle delete package
  const handleDeletePackage = async (id) => {
    try {
      const res = await axiosAdmin.delete(`/packs/${id}`);
      if (res) {
        setDifResponses(res);
        successToast(t("success"));
      }
    } catch (err) {
      setPackageDataErr(err);
      errorToast(apiError(err, t("error")));
    }
  };

  //const handle send package
  const handleSendSelectedPackages = async () => {
    // A flight belongs to one warehouse. With no warehouse chosen the list
    // shows every warehouse's parcels, and "select all" once put 27 sea and
    // land parcels on an air flight. The server refuses that now; this just
    // says so before the request.
    if (!currentPlaceId) {
      errorToast("ჯერ აირჩიეთ საწყობი");
      return;
    }
    if (!shipmentGeneralCode) {
      errorToast("აირჩიეთ რეისი");
      return;
    }
    try {
      const res = await axiosAdmin.post(`/packs/set`, {
        status: "2",
        ids: [...selectedPackages.pack_id],
        shipment_code: shipmentGeneralCode,
      });
      if (res) {
        successToast(t("admin_packages_sent"));
        setCheckAll(false);
        setSelectedPackages({
          pack_id: [],
        });
        setDifResponses(res);
      }
    } catch (err) {
      errorToast(apiError(err, t("admin_error")));
    }
  };

  //data fetching
  const handleParcelInfo = () => {
    setIsLoading(true);
    axiosAdmin
      .get(
        `/packs?status=${1}&abroad_office_id=${currentPlaceId}&page=${
          currentPage + 1
        }` +
          `&per_page=${30}&addon_transp=${
            transportation.transport
          }&searchValue=${search.key}` +
          `&secured=${transportation.secured}` +
          `&repack=${
            transportation.repack === "false" ? "" : transportation.repack
          }`
      )
      .then(({ data }) => {
        setPackageData(data.data);
        setTotal(data.meta.total);
        setIsLoading(false);
      })
      .catch((err) => {
        setPackageDataErr(err);
      });
  };

  //update user
  // A different recipient opens the address popup first: the delivery
  // address belongs to the customer, and the previous owner's must not ride
  // along. Same recipient saves straight away.
  const [recipientPrompt, setRecipientPrompt] = useState(null);
  const handleGiveUserToParcel = (newData, packId) => {
    const current = packageData.find((p) => p.id === packId);
    const nextUser = newData?.userId;
    if (nextUser && String(nextUser) !== String(current?.user_id ?? "")) {
      setRecipientPrompt({ newData, packId, userId: nextUser, tracking: current?.tracking_number });
      return;
    }
    saveParcelEdit(newData, packId);
  };

  const saveParcelEdit = (newData, packId, addressId) => {
    axiosAdmin
      .put(`/packs/${packId}`, {
        user_id: newData.userId,
        ...(addressId !== undefined ? { user_address_id: addressId } : {}),
        total_weight: newData.weight,
        shipment_code: newData.flightNumber,
        transport_price: newData.transport_price,
        dementionY: newData.dementionY,
        dementionZ: newData.dementionZ,
        dementionX: newData.dementionX,
        addon_secur: newData.addon_secur,
      })
      .then((res) => {
        successToast(t("admin_packages_sent"));
        setDifResponses(res);
        // The group changed, so siblings may have flipped colour on a page
        // the operator is not looking at.
        if (res?.data?.border_declaration?.length) {
          setBorderNotice(res.data.border_declaration);
        }
      })
      .catch(() => {
        errorToast(t("admin_error"));
      });
  };

  const handleUndeclared = (pack) => {
    axiosAdmin
      .get(`/packs/unDeclare/${pack}`)
      .then((res) => {
        successToast(t("admin_packages_sent"));
        setDifResponses(res);
      })
      .catch(() => {
        errorToast(t("admin_error"));
      });
  };

  //on barcode scanner using
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleParcelInfo();
    }
  };

  //send with flight number
  const handleSendWithFlight = () => {
    axiosAdmin
      .post("/packs/send", {
        shipment_code: shipmentGeneralCode,
        abroad_office_id: currentPlaceId,
      })
      .then((res) => {
        setDifResponses(res);
      });
  };

  //package data fetching
  useEffect(() => {
    handleParcelInfo();
  }, [currentPlaceId, difResponses, transportation, currentPage]);

  //package data fetching
  useEffect(() => {
    setCurrentPage(0);
  }, [search]);

  useEffect(() => {
    if (selectedPackages.pack_id.length > 0) {
      setCheckAll(false);
      setSelectedPackages({
        pack_id: [],
      });
    }
  }, [currentPage]);

  useEffect(() => {
    axiosAdmin
      .get(`/shipments?per_page=500`)
      .then(({ data }) => {
        // Keep the warehouse on each entry so the dropdown can be narrowed
        // to the warehouse on screen.
        setShipments(
          data.data.map((item) => ({
            value: item.code,
            name: item.code,
            abroad_office_id: item.abroad_office_id,
          }))
        );
      })
      .catch((err) => {});
  }, []);

  return (
    <div
      className="xl:ml-[280px] mt-[55px] bg-slate-100 dark:bg-slate-900 
            min-h-screen p-10 sm:p-5 pb-24 sm:pb-24"
    >
      <div className="w-full overflow-auto scrollbar mb-5">
        <div className="flex gap-1 w-full md:w-[1000px] p-2">
          {foreignAdresses.map((item) => {
            // Data-driven scope (new): if admin.abroad_office_id is set on the backend,
            // AdminContext already narrowed `foreignAdresses` to that single office,
            // so just render whatever we get. Legacy role→office hardcoding stays
            // below for admins created before abroad_office_id existed.
            if (scopedOfficeId || user.role === "100") {
              return (
                <FilialsCardWar
                  item={item}
                  key={item.id}
                  currentPlaceId={currentPlaceId}
                  handleCurrentPlaceId={handleCurrentPlaceId}
                  setDifResponses={setDifResponses}
                />
              );
            }
            // Legacy fallback for admins without abroad_office_id set:
            const legacyMap = { "1": 9, "2": 6, "3": 5, "4": 14, "5": 15 };
            if (legacyMap[user.role] === item.id) {
              return (
                <FilialsCardWar
                  item={item}
                  key={item.id}
                  currentPlaceId={currentPlaceId}
                  handleCurrentPlaceId={handleCurrentPlaceId}
                  setDifResponses={setDifResponses}
                />
              );
            }
            return null;
          })}
        </div>
      </div>
      <h2 className="text-slate-400 text-2xl">{t("admin_warehouse_packages")}</h2>
      <div className="w-full">
        <div className="bg-yellow mt-2 flex gap-1 sm:flex-col">
          <div
            className="bg-white  pv-2 flex items-center hover:outline-slate-400 hover:outline-2
            hover:outline gap-x-1  px-3.5 py-1 rounded-lg dark:bg-slate-800 border-gray-300
            border dark:border-slate-600 w-9/12 sm:w-full"
          >
            <input
              type="search"
              placeholder={t("search")}
              onChange={(e) => {
                handleSearchvalue(e, "key");
              }}
              className="w-full h-8 pl-1.5 focus:outline-none dark:text-white
                                dark:bg-slate-800 border-0"
              onKeyDown={handleKeyPress}
            />
          </div>

          <SelectInputValueId
            options={
              currentPlaceId
                ? shipments.filter((f) => Number(f.abroad_office_id) === Number(currentPlaceId))
                : shipments
            }
            inputName={"shipmentId"}
            inputSetter={(flight_num, _) => {
              setShipmentGeneralCode(flight_num);
            }}
            value={shipmentGeneralCode}
            defaultText={t("admin_general_flight_number")}
            needClear={true}
          />

          <AdminDownloadLink
            filename="packs.xlsx"
            href={
              `${process.env.REACT_APP_API_URL}/api/exportPacks?status=${1}` +
              `&abroad_office_id=${currentPlaceId}` +
              `&repack=${
                transportation.repack === "false" ? "" : transportation.repack
              }`
            }
            className="w-3/12 rounded-lg bg-green-400 sm:w-full flex items-center justify-center p-1 py-3 text-white"
          >
            EXPORT CSV
          </AdminDownloadLink>
        </div>
        <div className="flex items-center justify-between my-2 flex-wrap gap-3 mt-2">
          <div className="flex gap-2 items-center sm:order-2 sm:flex-col sm:items-start">
            <CheckBox
              name={t("admin_transportation")}
              inputName={"transport"}
              handleCarData={handleSearchTransport}
            />
            <CheckBox
              name={t("admin_insured_packages")}
              inputName={"secured"}
              handleCarData={handleSearchTransport}
            />
            <div className="flex items-center gap-3">
              <input
                checked={checkAll}
                onChange={(e) => {
                  handleCheckAll(e);
                }}
                type="checkbox"
              />
              <p className="text-sm text-gray-500">{t("admin_select_all")}</p>
            </div>
            <CheckBox
              name={t("admin_repacked_packages")}
              inputName={"repack"}
              handleCarData={handleSearchTransport}
            />
          </div>
          <div className="flex gap-2 sm:flex-wrap">
            <div
              onClick={handleSendSelectedPackages}
              className="rounded-lg bg-green-400 sm:w-full cursor-pointer
                 flex items-center justify-center gap-1 p-1 py-3 text-white"
            >
              <p>{t("admin_send_selected")}</p>
            </div>
          </div>
          <div className="flex gap-2 sm:flex-wrap">
            <div
              onClick={handleSendWithFlight}
              className="rounded-lg bg-green-400 sm:w-full cursor-pointer
                 flex items-center justify-center gap-1 p-1 py-3 text-white"
            >
              <p>{t("admin_send_by_flight")}</p>
            </div>
          </div>
        </div>
        <div className="w-full flex gap-3 flex-wrap mt-5">
          <PaginatedItems pageNumberSetter={setCurrentPage} dataLength={total}>
            {isLoading ? (
              <div className="mt-5 w-full flex items-center justify-center min-h-[200px]">
                <BiLoaderAlt className="w-20 h-20 animate-spin text-green-500" />
              </div>
            ) : isLoading === false && packageData.length === 0 ? (
              <div className="mt-5 w-full flex items-center justify-center min-h-[200px]">
                <p className="text-slate-600 text-sm">{t("admin_no_data_found")}</p>
              </div>
            ) : (
              <div className="w-full flex flex-col gap-2">
                {packageData.map((item) => {
                  return (
                    <WarehousePackageItem
                      key={item.id}
                      data={item}
                      handleDeletePackage={handleDeletePackage}
                      setCheckedAll={checkAll}
                      setDifResponses={setDifResponses}
                      handleSelectedPackages={handleSelectedPackages}
                      handleDeleteSelectedPackages={
                        handleDeleteSelectedPackages
                      }
                      handleGiveUserToParcel={handleGiveUserToParcel}
                      handleUndeclared={handleUndeclared}
                      currentPlaceType={currentPlaceType}
                    />
                  );
                })}
              </div>
            )}
          </PaginatedItems>
        </div>
      </div>
      <BorderChangeNotice
        reports={borderNotice}
        onClose={() => setBorderNotice(null)}
      />
      {recipientPrompt && (
        <RecipientAddressModal
          packId={recipientPrompt.packId}
          tracking={recipientPrompt.tracking}
          userId={recipientPrompt.userId}
          onCancel={() => setRecipientPrompt(null)}
          onConfirm={(addressId) => {
            saveParcelEdit(recipientPrompt.newData, recipientPrompt.packId, addressId);
            setRecipientPrompt(null);
          }}
        />
      )}
    </div>
  );
};

export default WarehousePackage;
